import React, { useState } from 'react';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { productsApi } from '../api/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

interface FavoriteButtonProps {
  productId: number;
  initialFavorite?: boolean;
  size?: 'small' | 'medium' | 'large';
  color?: string;
  sx?: any;
}

const FavoriteButton: React.FC<FavoriteButtonProps> = ({
  productId,
  initialFavorite = false,
  size = 'medium',
  color = 'error',
  sx = {}
}) => {
  const [isFavorite, setIsFavorite] = useState(initialFavorite);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // If user is not logged in, redirect to login page
    if (!isAuthenticated) {
      navigate('/login', { state: { from: window.location.pathname } });
      return;
    }

    setLoading(true);
    try {
      const newFavoriteStatus = await productsApi.toggleFavorite(productId, isFavorite);
      setIsFavorite(newFavoriteStatus);
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip title={isFavorite ? "Remove from favorites" : "Add to favorites"}>
      <IconButton
        aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
        onClick={handleToggleFavorite}
        size={size}
        disabled={loading}
        sx={{ 
          color: isFavorite ? `${color}.main` : 'text.secondary',
          ...sx
        }}
      >
        {loading ? (
          <CircularProgress size={size === 'small' ? 18 : 24} color="inherit" />
        ) : isFavorite ? (
          <FavoriteIcon fontSize={size} />
        ) : (
          <FavoriteBorderIcon fontSize={size} />
        )}
      </IconButton>
    </Tooltip>
  );
};

export default FavoriteButton; 